import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config';



const AdminContext = createContext();

export const useAdmin = () => useContext(AdminContext);

export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showContentPanel, setShowContentPanel] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Content management state
  const [siteContent, setSiteContent] = useState({});
  const [contentLoading, setContentLoading] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState({});

  // Check for existing session on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('admin_session');
    if (savedSession) {
      verifySession(savedSession);
    } else {
      setLoading(false);
    }
  }, []);

  // Load content when admin logs in
  useEffect(() => {
    if (isAdmin) {
      loadAllContent();
    }
  }, [isAdmin]);

  const verifySession = useCallback(async (sid) => {
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-session/${sid}`);
      if (res.ok) {
        setSessionId(sid);
        setIsAdmin(true);
      } else {
        localStorage.removeItem('admin_session');
      }
    } catch (error) {
      console.error('Session verification failed:', error);
      localStorage.removeItem('admin_session');
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.detail || 'Ошибка авторизации');
    }
    
    const data = await res.json();
    setSessionId(data.session_id);
    setIsAdmin(true);
    localStorage.setItem('admin_session', data.session_id);
    setShowLoginModal(false);
    return data;
  };

  const logout = async () => {
    if (sessionId) {
      try {
        await fetch(`${API_URL}/api/auth/logout/${sessionId}`, { method: 'POST' });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    setSessionId(null);
    setIsAdmin(false);
    setIsEditMode(false);
    setShowContentPanel(false);
    localStorage.removeItem('admin_session');
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  // ==================== Content Management ====================

  const loadAllContent = useCallback(async () => {
    setContentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/content`);
      if (res.ok) {
        const data = await res.json();
        // Organize content by section
        const organized = {};
        data.forEach(item => {
          if (!organized[item.section]) {
            organized[item.section] = {};
          }
          organized[item.section][item.key] = item;
        });
        setSiteContent(organized);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    }
    setContentLoading(false);
  }, []);

  const getContent = useCallback((section, key, language = 'ru') => {
    const item = siteContent[section]?.[key];
    if (!item) return null;
    
    const value = item.value;
    if (typeof value === 'object' && value[language]) {
      return value[language];
    }
    return value?.text || value;
  }, [siteContent]);

  const getContentItem = useCallback((section, key) => {
    return siteContent[section]?.[key] || null;
  }, [siteContent]);

  const updateContentLocal = (itemId, newValue) => {
    // Update local state
    setSiteContent(prev => {
      const updated = { ...prev };
      for (const section in updated) {
        for (const key in updated[section]) {
          if (updated[section][key].id === itemId) {
            updated[section][key] = {
              ...updated[section][key],
              value: newValue
            };
          }
        }
      }
      return updated;
    });
    
    // Track unsaved changes
    setUnsavedChanges(prev => ({
      ...prev,
      [itemId]: newValue
    }));
  };

  const saveContent = async (itemId, value) => {
    if (!sessionId) return false;
    
    try {
      const res = await fetch(`${API_URL}/api/content/item/${itemId}?session_id=${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(value)
      });
      
      if (res.ok) {
        // Remove from unsaved changes
        setUnsavedChanges(prev => {
          const updated = { ...prev };
          delete updated[itemId];
          return updated;
        });
        return true;
      }
    } catch (error) {
      console.error('Failed to save content:', error);
    }
    return false;
  };

  const saveAllChanges = async () => {
    if (!sessionId || Object.keys(unsavedChanges).length === 0) return false;
    
    try {
      const items = Object.entries(unsavedChanges).map(([id, value]) => ({
        id,
        value
      }));
      
      const res = await fetch(`${API_URL}/api/content/bulk?session_id=${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      
      if (res.ok) {
        setUnsavedChanges({});
        return true;
      }
    } catch (error) {
      console.error('Failed to save all changes:', error);
    }
    return false;
  };

  const resetToDefault = async (itemId) => {
    if (!sessionId) return false;
    
    try {
      const res = await fetch(`${API_URL}/api/content/reset/${itemId}?session_id=${sessionId}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        await loadAllContent();
        return true;
      }
    } catch (error) {
      console.error('Failed to reset content:', error);
    }
    return false;
  };

  const uploadImage = async (file) => {
    if (!sessionId) return null;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API_URL}/api/upload/image?session_id=${sessionId}`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        return `${API_URL}${data.url}`;
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
    return null;
  };

  const initDefaultContent = async () => {
    if (!sessionId) return false;
    
    try {
      const res = await fetch(`${API_URL}/api/content/init?session_id=${sessionId}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        await loadAllContent();
        return true;
      }
    } catch (error) {
      console.error('Failed to init content:', error);
    }
    return false;
  };

  const value = {
    // Auth
    isAdmin,
    isEditMode,
    sessionId,
    loading,
    showLoginModal,
    setShowLoginModal,
    login,
    logout,
    toggleEditMode,
    
    // Content Panel
    showContentPanel,
    setShowContentPanel,
    
    // Content Management
    siteContent,
    contentLoading,
    unsavedChanges,
    loadAllContent,
    getContent,
    getContentItem,
    updateContentLocal,
    saveContent,
    saveAllChanges,
    resetToDefault,
    uploadImage,
    initDefaultContent,
    
    // Helpers
    hasUnsavedChanges: Object.keys(unsavedChanges).length > 0
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
